import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let polls = 0;
    let timer;
    const poll = async () => {
      polls += 1;
      try {
        const res = await api.get(`/billing/status/${sessionId}`);
        if (res.data.payment_status === "paid") { await refresh(); setStatus("paid"); return; }
        if (["failed", "expired"].includes(res.data.payment_status)) { setStatus("error"); return; }
      } catch { /* keep polling */ }
      if (polls >= MAX_POLLS) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-10 text-center animate-fade-up" data-testid="payment-success">
        {status === "polling" && (<><Loader2 className="h-12 w-12 text-[#202123] mx-auto mb-4 animate-spin" /><p className="font-display text-lg font-semibold text-navy">{t("payment.confirming")}</p></>)}
        {status === "paid" && (<>
          <CheckCircle2 className="h-14 w-14 text-[#202123] mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-navy">{t("payment.allSet")}</h1>
          <p className="text-slatebody mt-2">{t("payment.subActive")}</p>
          <Button className="mt-6 bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-1.5" onClick={() => navigate("/app/log")} data-testid="success-continue"><Sparkles className="h-4 w-4" /> {t("payment.goToJotBay")}</Button>
        </>)}
        {(status === "error" || status === "timeout") && (<>
          <XCircle className="h-14 w-14 text-amber-500 mx-auto mb-4" />
          <h1 className="font-display text-xl font-bold text-navy">{status === "timeout" ? t("payment.stillProcessing") : t("payment.notCompleted")}</h1>
          <p className="text-slatebody mt-2">{status === "timeout" ? t("payment.timeoutMsg") : t("payment.errorMsg")}</p>
          <Button variant="outline" className="mt-6" onClick={() => navigate("/app/billing")} data-testid="success-back">{t("payment.backToBilling")}</Button>
        </>)}
      </div>
    </div>
  );
}
